import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Pagination,
  RadixSelect,
  SectionCard,
  Skeleton,
  Switch,
  toast,
} from '@/components/ui';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import {
  type IntegrationApiError,
  useCreateRecipientAllowlistMutation,
  useDeleteRecipientAllowlistMutation,
  useRecipientAllowlistQuery,
  useRecipientAllowlistSettingsQuery,
  useUpdateRecipientAllowlistSettingsMutation,
} from '../hooks/useIntegrationQuery';
import type { RecipientAllowlistEntry, RecipientType } from '../types/notificationIntegration';

/*
 * 알림 설정 > 연동 > 수신자 허용 목록(테스트 모드) — notification V37(BE PR #3).
 * QA·오픈 전 운영 DB 에는 이관된 실제 고객 연락처가 있어, 테스트 중 문자·알림톡·메일이 실제 고객에게 나갈 수 있다.
 * 모드를 켜면 목록의 번호·메일·테스트 회원 계정으로만 발송되고 나머지는 막힌다(발송 내역 "허용 목록 차단").
 * 기존 "발송 제한"(야간·중복 차단)과는 별개 API·별개 영역이다.
 */

const PAGE_SIZE = 20;
const TYPES: ReadonlyArray<RecipientType> = ['PHONE', 'EMAIL', 'ACCOUNT'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ko-KR');
};

interface Draft {
  recipientType: RecipientType;
  value: string;
  userId: string | null;
  label: string;
}
const EMPTY_DRAFT: Draft = { recipientType: 'PHONE', value: '', userId: null, label: '' };

export const RecipientAllowlistSection = ({ canWrite }: { canWrite: boolean }) => {
  const { t } = useTranslation('notifications');
  const typeLabel = (type: RecipientType) =>
    ({
      PHONE: t('integration.allowlist.type.PHONE', '번호'),
      EMAIL: t('integration.allowlist.type.EMAIL', '메일'),
      ACCOUNT: t('integration.allowlist.type.ACCOUNT', '회원 계정'),
    })[type];

  const settings = useRecipientAllowlistSettingsQuery();
  const updateSettings = useUpdateRecipientAllowlistSettingsMutation();
  const [filter, setFilter] = useState<RecipientType | null>(null);
  const [page, setPage] = useState(1);
  const list = useRecipientAllowlistQuery(filter, page - 1, PAGE_SIZE);
  const create = useCreateRecipientAllowlistMutation();
  const remove = useDeleteRecipientAllowlistMutation();

  const [confirmMode, setConfirmMode] = useState<boolean | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<RecipientAllowlistEntry | null>(null);

  // BE(notification PR #3) 배포 전에는 API 가 없어 404 — 오류 대신 "서버 반영 후" 안내만 보이고 목록·등록을 숨긴다.
  const notDeployed = settings.isError && settings.error?.status === 404;
  const s = settings.data;
  const enabled = s?.enabled ?? false;
  const noPhoneTargets = (s?.phoneCount ?? 0) === 0 && (s?.accountCount ?? 0) === 0;

  const applyMode = (next: boolean) =>
    updateSettings.mutate(
      { enabled: next },
      {
        onSuccess: () => {
          toast.success(
            next
              ? t('integration.allowlist.toast.enabled', '테스트 모드를 켰습니다. 등록된 수신자에게만 발송됩니다.')
              : t('integration.allowlist.toast.disabled', '테스트 모드를 껐습니다. 모든 수신자에게 발송됩니다.'),
          );
          setConfirmMode(null);
        },
        onError: (e) => toast.error(e.message || t('integration.allowlist.toast.modeFail', '테스트 모드 변경에 실패했습니다.')),
      },
    );

  const createError = (e: IntegrationApiError): string => {
    if (e.status === 409) return t('integration.allowlist.error.duplicate', '이미 등록된 수신자입니다.');
    if (e.status === 400) return e.message || t('integration.allowlist.error.invalid', '형식이 맞지 않거나 없는 회원입니다.');
    return e.message || t('integration.allowlist.error.create', '수신자 등록에 실패했습니다.');
  };

  const submit = () => {
    if (!draft) return;
    setFormError(null);
    let recipientValue = '';
    if (draft.recipientType === 'ACCOUNT') {
      if (!draft.userId) return setFormError(t('integration.allowlist.error.account', '회원을 선택해 주세요.'));
      recipientValue = draft.userId;
    } else if (draft.recipientType === 'PHONE') {
      const digits = draft.value.replace(/\D/g, '');
      if (digits.length < 9 || digits.length > 15) {
        return setFormError(t('integration.allowlist.error.phone', '휴대폰 번호를 확인해 주세요.'));
      }
      recipientValue = draft.value.trim();
    } else {
      if (!EMAIL_RE.test(draft.value.trim())) {
        return setFormError(t('integration.allowlist.error.email', '메일 주소를 확인해 주세요.'));
      }
      recipientValue = draft.value.trim();
    }
    const label = draft.label.trim();
    create.mutate(
      { recipientType: draft.recipientType, recipientValue, ...(label ? { label } : {}) },
      {
        onSuccess: () => {
          toast.success(t('integration.allowlist.toast.created', '수신자를 등록했습니다.'));
          setDraft(null);
        },
        onError: (e) => setFormError(createError(e)),
      },
    );
  };

  return (
    <SectionCard
      title={t('integration.allowlist.title', '수신자 허용 목록 (테스트 모드)')}
      description={t(
        'integration.allowlist.description',
        '켜면 아래 목록의 번호·메일·테스트 회원 계정으로만 문자·알림톡·메일이 발송되고, 나머지는 막힙니다. QA·오픈 전 운영에서 실제 고객에게 알림이 나가지 않게 할 때 씁니다.',
      )}
      aside={
        canWrite && !notDeployed ? (
          <Button size="sm" variant="outline" onClick={() => { setFormError(null); setDraft({ ...EMPTY_DRAFT }); }} data-testid="allowlist-add">
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('integration.allowlist.add', '수신자 등록')}
          </Button>
        ) : undefined
      }
      data-testid="recipient-allowlist-section"
    >
      {notDeployed ? (
        <EmptyState
          size="inline"
          title={t('integration.allowlist.notDeployed', '서버 반영 후 사용할 수 있습니다.')}
          data-testid="allowlist-not-deployed"
        />
      ) : settings.isPending ? (
        <Skeleton className="h-[60px] w-full" />
      ) : settings.isError ? (
        <EmptyState size="inline" title={t('integration.allowlist.error.settings', '수신자 허용 목록 설정을 불러오지 못했습니다.')} />
      ) : (
        <div className="mb-4 flex flex-col gap-3 rounded-md p-4" style={{ background: 'var(--surface-1)' }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Switch
                checked={enabled}
                disabled={!canWrite || updateSettings.isPending}
                onCheckedChange={(v) => setConfirmMode(v)}
                aria-label={t('integration.allowlist.mode', '테스트 모드')}
                data-testid="allowlist-mode-switch"
              />
              <span className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
                {t('integration.allowlist.mode', '테스트 모드')}
              </span>
              {enabled ? (
                <Badge variant="warn" data-testid="allowlist-mode-badge">
                  {t('integration.allowlist.badge', '테스트 모드 — 등록된 수신자에게만 발송')}
                </Badge>
              ) : (
                <Badge variant="neutral">{t('integration.allowlist.off', '꺼짐 — 모든 수신자에게 발송')}</Badge>
              )}
            </div>
            <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }} data-testid="allowlist-counts">
              {t('integration.allowlist.counts', {
                defaultValue: '번호 {{phone}} · 메일 {{email}} · 회원 계정 {{account}}',
                phone: s?.phoneCount ?? 0,
                email: s?.emailCount ?? 0,
                account: s?.accountCount ?? 0,
              })}
            </span>
          </div>
          <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('integration.allowlist.signupHint', '회원가입 인증 문자처럼 계정이 생기기 전에 나가는 발송은 번호로 등록해야 합니다.')}
          </p>
        </div>
      )}

      {notDeployed ? null : (
      <>
      <div className="mb-3 flex items-center gap-2">
        <RadixSelect
          value={filter ?? 'ALL'}
          onChange={(v) => { setFilter(v === 'ALL' ? null : (v as RecipientType)); setPage(1); }}
          options={[
            { value: 'ALL', label: t('integration.allowlist.filterAll', '전체') },
            ...TYPES.map((type) => ({ value: type, label: typeLabel(type) })),
          ]}
          className="w-36"
          aria-label={t('integration.allowlist.filter', '종류')}
          data-testid="allowlist-filter"
        />
      </div>

      {list.isPending ? (
        <Skeleton className="h-[120px] w-full" />
      ) : list.isError ? (
        <EmptyState size="inline" title={t('integration.allowlist.error.list', '수신자 허용 목록을 불러오지 못했습니다.')} />
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <EmptyState size="inline" title={t('integration.allowlist.empty', '등록된 수신자가 없습니다.')} />
      ) : (
        <>
          <table className="w-full text-[13px]" data-testid="allowlist-table">
            <thead>
              <tr style={{ color: 'var(--content-muted)' }}>
                <th className="px-3 py-2 text-left font-medium">{t('integration.allowlist.col.type', '종류')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('integration.allowlist.col.value', '수신자')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('integration.allowlist.col.label', '메모')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('integration.allowlist.col.createdAt', '등록일')}</th>
                {canWrite ? <th className="w-12 px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((e) => (
                <tr key={e.allowlistId} className="border-t" style={{ borderColor: 'var(--divider-soft)' }} data-testid="allowlist-row">
                  <td className="px-3 py-2">{typeLabel(e.recipientType)}</td>
                  <td className="px-3 py-2">
                    {/* 회원 계정은 로그인ID(label)를 주로 보이고 userId 는 작게. */}
                    {e.recipientType === 'ACCOUNT' ? (
                      <>
                        <div style={{ color: 'var(--content-strong)' }}>{e.label || '—'}</div>
                        <div className="text-[11px]" style={{ color: 'var(--content-muted)' }}>{e.recipientValue}</div>
                      </>
                    ) : (
                      <span className="tabular-nums" style={{ color: 'var(--content-strong)' }}>{e.recipientValue}</span>
                    )}
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--content-muted)' }}>
                    {e.recipientType === 'ACCOUNT' ? '—' : e.label || '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums" style={{ color: 'var(--content-muted)' }}>{formatDate(e.createdAt)}</td>
                  {canWrite ? (
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleting(e)}
                        aria-label={t('integration.allowlist.delete', '삭제')}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            className="mt-3"
            total={list.data?.total ?? 0}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            showGoTo={false}
          />
        </>
      )}
      </>
      )}

      {/* 모드 켜기/끄기 확인 */}
      <Dialog open={confirmMode !== null} onOpenChange={(o) => !o && setConfirmMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmMode
                ? t('integration.allowlist.confirmOn.title', '테스트 모드를 켤까요?')
                : t('integration.allowlist.confirmOff.title', '테스트 모드를 끌까요?')}
            </DialogTitle>
            <DialogDescription>
              {confirmMode
                ? t('integration.allowlist.confirmOn.description', '관리자 2FA 를 받는 직원 번호가 목록에 있는지 확인하세요. 없으면 2FA 문자도 차단됩니다.')
                : t('integration.allowlist.confirmOff.description', '모든 수신자에게 실제 발송됩니다.')}
            </DialogDescription>
          </DialogHeader>
          {confirmMode && noPhoneTargets ? (
            <p
              role="alert"
              className="rounded-md p-3 text-[13px] font-medium"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
              data-testid="allowlist-empty-warning"
            >
              {t(
                'integration.allowlist.confirmOn.emptyWarning',
                '등록된 번호와 회원 계정이 하나도 없습니다. 켜면 모든 문자·알림톡이 막히고, 관리자 2FA 문자도 받을 수 없어 로그인하지 못할 수 있습니다.',
              )}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('common.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant={confirmMode ? 'primary' : 'danger'}
              size="sm"
              disabled={updateSettings.isPending}
              onClick={() => confirmMode !== null && applyMode(confirmMode)}
              data-testid="allowlist-mode-confirm"
            >
              {confirmMode ? t('integration.allowlist.confirmOn.action', '켜기') : t('integration.allowlist.confirmOff.action', '끄기')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 수신자 등록 */}
      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integration.allowlist.addTitle', '수신자 등록')}</DialogTitle>
            <DialogDescription>
              {t('integration.allowlist.signupHint', '회원가입 인증 문자처럼 계정이 생기기 전에 나가는 발송은 번호로 등록해야 합니다.')}
            </DialogDescription>
          </DialogHeader>
          {draft ? (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <div className="flex gap-2" role="radiogroup" aria-label={t('integration.allowlist.filter', '종류')}>
                {TYPES.map((type) => (
                  <Button
                    key={type}
                    type="button"
                    size="sm"
                    variant={draft.recipientType === type ? 'primary' : 'outline'}
                    role="radio"
                    aria-checked={draft.recipientType === type}
                    onClick={() => setDraft({ ...EMPTY_DRAFT, recipientType: type })}
                    data-testid={`allowlist-type-${type}`}
                  >
                    {typeLabel(type)}
                  </Button>
                ))}
              </div>
              {draft.recipientType === 'ACCOUNT' ? (
                <div className="flex flex-col gap-1 text-sm">
                  {t('integration.allowlist.accountLabel', '회원')}
                  <UserSearchSelect
                    value={draft.userId}
                    onChange={(userId) => setDraft((d) => (d ? { ...d, userId } : d))}
                    testId="allowlist-user-select"
                    ariaLabel={t('integration.allowlist.accountLabel', '회원')}
                  />
                  <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {t('integration.allowlist.accountHint', '그 회원에게 지금 저장된 휴대폰·메일로 발송됩니다. 번호를 바꾸면 자동으로 따라갑니다(최대 1분).')}
                  </span>
                </div>
              ) : (
                <label className="flex flex-col gap-1 text-sm">
                  {draft.recipientType === 'PHONE'
                    ? t('integration.allowlist.phoneLabel', '휴대폰 번호')
                    : t('integration.allowlist.emailLabel', '메일 주소')}
                  <Input
                    value={draft.value}
                    onChange={(e) => setDraft((d) => (d ? { ...d, value: e.target.value } : d))}
                    placeholder={draft.recipientType === 'PHONE' ? '010-1234-5678' : 'qa@example.com'}
                    inputMode={draft.recipientType === 'PHONE' ? 'tel' : 'email'}
                    maxLength={255}
                    data-testid="allowlist-value"
                  />
                </label>
              )}
              {draft.recipientType !== 'ACCOUNT' ? (
                <label className="flex flex-col gap-1 text-sm">
                  {t('integration.allowlist.col.label', '메모')}
                  <Input
                    value={draft.label}
                    onChange={(e) => setDraft((d) => (d ? { ...d, label: e.target.value } : d))}
                    placeholder={t('integration.allowlist.labelPlaceholder', '예: QA 홍길동')}
                    maxLength={100}
                    data-testid="allowlist-label"
                  />
                </label>
              ) : null}
              {formError ? (
                <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>{formError}</p>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={create.isPending}>
                  {t('common.cancel', '취소')}
                </Button>
                <Button type="submit" variant="primary" size="sm" disabled={create.isPending} data-testid="allowlist-submit">
                  {t('integration.allowlist.submit', '등록')}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* 삭제 확인 */}
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integration.allowlist.deleteTitle', '수신자 삭제')}</DialogTitle>
            <DialogDescription>
              {t('integration.allowlist.deleteDescription', '테스트 모드가 켜져 있으면 이 수신자에게도 발송이 막힙니다.')}
            </DialogDescription>
          </DialogHeader>
          {deleting ? (
            <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
              {typeLabel(deleting.recipientType)} · {deleting.recipientType === 'ACCOUNT' ? deleting.label || deleting.recipientValue : deleting.recipientValue}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('common.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate(deleting.allowlistId, {
                  onSuccess: () => {
                    toast.success(t('integration.allowlist.toast.deleted', '수신자를 삭제했습니다.'));
                    setDeleting(null);
                  },
                  onError: (e) => toast.error(e.message || t('integration.allowlist.error.delete', '수신자 삭제에 실패했습니다.')),
                });
              }}
              data-testid="allowlist-delete-confirm"
            >
              {t('common.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
